import { createSvgEl } from '@learnforge/demo-render';
import { hypercubeCoords } from '../sets';

// ── Layout constants ─────────────────────────────────────────────────────────

const NODE_R = 20;
const W = 400;
const H = 380;
const PAD_X = 60;
const PAD_Y = 50;

// ── Public API ───────────────────────────────────────────────────────────────

export interface HasseNode {
  rank: number;
  label: string;
}

export interface HasseLatticeOptions {
  nodes: readonly HasseNode[];
  edges: readonly (readonly [number, number])[];
  highlightRank?: number | null;
  cube?: boolean;
  onSelectRank?(rank: number): void;
}

export interface HasseLatticeHandle {
  group: SVGGElement;
  update(opts: Partial<HasseLatticeOptions>): void;
  destroy(): void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Popcount (number of set bits). Used to determine the Hasse level of a rank. */
function popcount(n: number): number {
  let count = 0;
  let v = n;
  while (v) {
    count += v & 1;
    v >>= 1;
  }
  return count;
}

/**
 * Compute (x, y) positions for each node in the default Hasse layout.
 * Nodes are grouped by popcount(rank) = their subset-size level.
 * ∅ (level 0) is at the bottom; the full set is at the top.
 */
function hassePositions(nodes: readonly HasseNode[]): Map<number, { x: number; y: number }> {
  // Group nodes by level.
  const byLevel = new Map<number, number[]>();
  for (const node of nodes) {
    const level = popcount(node.rank);
    const arr = byLevel.get(level) ?? [];
    arr.push(node.rank);
    byLevel.set(level, arr);
  }

  const levels = [...byLevel.keys()].sort((a, b) => a - b);
  const maxLevel = levels[levels.length - 1] ?? 0;
  const levelCount = maxLevel + 1;

  const positions = new Map<number, { x: number; y: number }>();

  for (const level of levels) {
    const ranks = byLevel.get(level) ?? [];
    const count = ranks.length;
    const usableW = W - PAD_X * 2;
    const usableH = H - PAD_Y * 2;

    // y: level 0 → bottom (H - PAD_Y), max level → top (PAD_Y)
    const y = levelCount > 1 ? H - PAD_Y - (level / (levelCount - 1)) * usableH : H / 2;

    for (let i = 0; i < count; i++) {
      // Spread horizontally, centred.
      const x = count > 1 ? PAD_X + (i / (count - 1)) * usableW : W / 2;
      positions.set(ranks[i] as number, { x, y });
    }
  }

  return positions;
}

/**
 * Compute (x, y) positions for cube layout using hypercubeCoords projected
 * with a simple isometric (oblique) projection.
 * Works for n = 0..5 (up to 32 nodes). For n > 5 falls back to Hasse layout.
 */
function cubePositions(nodes: readonly HasseNode[]): Map<number, { x: number; y: number }> {
  // Determine n from the number of nodes: |nodes| = 2^n
  const total = nodes.length;
  const n = Math.round(Math.log2(Math.max(total, 1)));
  if (n > 5 || 1 << n !== total) {
    return hassePositions(nodes);
  }

  const coords = hypercubeCoords(n);
  const coordMap = new Map(coords.map(({ rank, coords: c }) => [rank, c]));

  // Project n-dimensional bit-vector to 2D using simple oblique basis vectors.
  // Basis vectors per dimension (isometric-style):
  const angleStep = (2 * Math.PI) / Math.max(n, 1);
  const basisX: number[] = [];
  const basisY: number[] = [];
  for (let d = 0; d < n; d++) {
    const angle = -Math.PI / 2 + d * angleStep;
    basisX.push(Math.cos(angle));
    basisY.push(Math.sin(angle));
  }

  // Compute raw projected coords.
  const raw: { rank: number; x: number; y: number }[] = [];
  for (const node of nodes) {
    const c = coordMap.get(node.rank) ?? Array.from<number>({ length: n }).fill(0);
    let px = 0;
    let py = 0;
    for (let d = 0; d < n; d++) {
      px += (c[d] ?? 0) * (basisX[d] ?? 0);
      py += (c[d] ?? 0) * (basisY[d] ?? 0);
    }
    raw.push({ rank: node.rank, x: px, y: py });
  }

  // Normalise to fit the canvas.
  if (raw.length === 0) return new Map();

  const minX = Math.min(...raw.map((r) => r.x));
  const maxX = Math.max(...raw.map((r) => r.x));
  const minY = Math.min(...raw.map((r) => r.y));
  const maxY = Math.max(...raw.map((r) => r.y));

  const rangeX = maxX - minX || 1;
  const rangeY = maxY - minY || 1;
  const usableW = W - PAD_X * 2;
  const usableH = H - PAD_Y * 2;

  const positions = new Map<number, { x: number; y: number }>();
  for (const { rank, x, y } of raw) {
    positions.set(rank, {
      x: PAD_X + ((x - minX) / rangeX) * usableW,
      y: PAD_Y + ((y - minY) / rangeY) * usableH,
    });
  }
  return positions;
}

// ── createHasseLattice ───────────────────────────────────────────────────────

/**
 * Renders a Hasse diagram (power-set lattice) as an SVG group.
 *
 * - Default layout: nodes positioned by popcount level (∅ at bottom, full set
 *   at top); edges as `<line data-edge>`; nodes as `<g data-rank>` with a
 *   circle and label.
 * - Cube layout (`cube: true`): positions nodes via hypercubeCoords projected
 *   to 2D with a simple oblique/isometric projection.
 * - `highlightRank` adds `is-highlight` to the matching node's `<g>`.
 * - `onSelectRank` fires on click of any node's `<g>`.
 * - No inline colours.
 */
export function createHasseLattice(
  parent: SVGSVGElement | SVGGElement,
  opts: HasseLatticeOptions,
): HasseLatticeHandle {
  let current: HasseLatticeOptions = { ...opts };

  const group = createSvgEl('g', { class: 'hl-lattice' });
  parent.appendChild(group);

  function render(): void {
    group.replaceChildren();

    const positions = current.cube ? cubePositions(current.nodes) : hassePositions(current.nodes);

    // ── Edges (drawn first, so they sit beneath nodes) ──
    for (const [lo, hi] of current.edges) {
      const posLo = positions.get(lo);
      const posHi = positions.get(hi);
      if (!posLo || !posHi) continue;

      const line = createSvgEl('line', {
        class: 'hl-edge',
        'data-edge': `${lo}:${hi}`,
        x1: posLo.x,
        y1: posLo.y,
        x2: posHi.x,
        y2: posHi.y,
      });
      group.appendChild(line);
    }

    // ── Nodes ──
    for (const node of current.nodes) {
      const pos = positions.get(node.rank);
      if (!pos) continue;

      const isHighlighted = current.highlightRank === node.rank;
      const g = createSvgEl('g', {
        class: isHighlighted ? 'hl-node is-highlight' : 'hl-node',
        'data-rank': node.rank,
      });

      const circle = createSvgEl('circle', {
        class: 'hl-node-circle',
        cx: pos.x,
        cy: pos.y,
        r: NODE_R,
      });

      const text = createSvgEl('text', {
        class: 'hl-node-label',
        x: pos.x,
        y: pos.y,
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
      });
      text.textContent = node.label;

      g.appendChild(circle);
      g.appendChild(text);

      if (current.onSelectRank) {
        const rank = node.rank;
        g.addEventListener('click', () => current.onSelectRank?.(rank));
      }

      group.appendChild(g);
    }
  }

  render();

  return {
    group,

    update(partial: Partial<HasseLatticeOptions>): void {
      current = { ...current, ...partial };
      render();
    },

    destroy(): void {
      group.remove();
    },
  };
}
